import { CapabilityFieldRecord } from '../api/capability-service';

/**
 * x-options bridge — converts a capability version's INPUT
 * {@link CapabilityFieldRecord}[] into the JSON-schema dialect
 * {@code SchemaFormComponent} renders, carrying the bespoke `x-options` its
 * widget-mapper reads. This is what turns a capability descriptor into the
 * "schedule a task" form.
 *
 * Per-field leaf mapping:
 *   - Connector field      → {type:'string', x-options:{source:'connector-catalog', connectorType:'<type>:<subtype>'}}
 *                            → SchemaFormComponent renders a connection picker over
 *                              nucleus-connectors-service GET /connections?type=&subtype=.
 *   - Data dataType=userdata → {type:'string', x-options:{source:'userdata', filter:{type:<userdataType>, …}}}
 *   - Data dataType=enum     → {type:'string', enum:[…]}
 *   - Data dataType=boolean  → {type:'boolean'}  (slide-toggle / boolean-list)
 *   - Data dataType=number   → {type:'number'}
 *   - Data dataType=string   → {type:'string'}
 *
 * Shared per-field meta: required[], default (parsed defaultValueJson), title
 * (humanised fieldName), description, x-order (fieldOrder), x-group (groupName),
 * x-options.resource (RM-leased) + x-options.secret (masked).
 *
 * cardinality='list' wraps the leaf as {type:'array', items:leaf}. OUTPUT fields
 * are skipped — only INPUT drives the schedule-creation form.
 */
export interface JsonObjectSchema {
  type: 'object';
  properties: Record<string, any>;
  required?: string[];
}

const ROLE = CapabilityFieldRecord.FieldRoleEnum;
const KIND = CapabilityFieldRecord.FieldKindEnum;
const CARD = CapabilityFieldRecord.CardinalityEnum;

export function buildSchemaFromCapabilityFields(
  fields: CapabilityFieldRecord[] | null | undefined,
): JsonObjectSchema {
  const schema: JsonObjectSchema = { type: 'object', properties: {}, required: [] };

  const inputs = (fields ?? [])
    .filter(f => f.fieldRole === ROLE.Input)
    .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0));

  for (const f of inputs) {
    const leaf = buildLeaf(f);
    const node: any =
      f.cardinality === CARD.List ? { type: 'array', items: leaf } : leaf;

    // For a list field, lift x-options onto the array node too so the form's
    // secret/resource treatment still applies to the whole field.
    if (node !== leaf && leaf['x-options']) node['x-options'] = leaf['x-options'];

    node.title = humanize(f.fieldName);
    if (f.description) node.description = f.description;
    if (typeof f.fieldOrder === 'number') node['x-order'] = f.fieldOrder;
    if (f.groupName) node['x-group'] = f.groupName;

    const def = parseJson(f.defaultValueJson);
    if (def !== undefined) node.default = def;

    schema.properties[f.fieldName] = node;
    if (f.required) schema.required!.push(f.fieldName);
  }

  if (!schema.required!.length) delete schema.required;
  return schema;
}

function buildLeaf(f: CapabilityFieldRecord): any {
  const xopts: any = {};
  if (isResource(f)) xopts.resource = true;
  if (isSecret(f)) xopts.secret = true;

  let leaf: any;

  if (f.fieldKind === KIND.Connector) {
    xopts.source = 'connector-catalog';
    if (f.connectorType) xopts.connectorType = f.connectorType; // '<type>:<subtype>'
    leaf = { type: 'string' };
  } else {
    switch (f.dataType) {
      case 'userdata':
        xopts.source = 'userdata';
        xopts.filter = { type: f.userdataType ?? '', ...(parseJson(f.userdataAttrFilterJson) ?? {}) };
        leaf = { type: 'string' };
        break;
      case 'enum':
        leaf = { type: 'string', enum: parseJson(f.enumValuesJson) ?? [] };
        break;
      case 'boolean':
        leaf = { type: 'boolean' };
        break;
      case 'number':
        leaf = { type: 'number' };
        break;
      default:
        leaf = { type: 'string' };
    }
  }

  if (Object.keys(xopts).length) leaf['x-options'] = xopts;
  return leaf;
}

/** isResource on the record (handles the resource/isResource serialization drift). */
function isResource(f: CapabilityFieldRecord): boolean {
  return f.isResource === true || (f as any).resource === true;
}

/** isSecret on the record (handles the secret/isSecret serialization drift). */
function isSecret(f: CapabilityFieldRecord): boolean {
  return f.isSecret === true || (f as any).secret === true;
}

/** Parse a JSON column value; undefined when absent / blank / malformed. */
function parseJson(raw: string | null | undefined): any {
  if (raw === null || raw === undefined || raw === '') return undefined;
  try { return JSON.parse(raw); } catch { return undefined; }
}

/** "neHostName" / "ne_host_name" → "Ne Host Name" for a readable field title. */
function humanize(name: string): string {
  if (!name) return name;
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}
